import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  LOCALE_ID,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Chart } from 'chart.js';
import 'chartjs-adapter-date-fns';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChartModule, UIChart } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { Divider } from 'primeng/divider';
import { FloatLabel } from 'primeng/floatlabel';
import { Fluid } from 'primeng/fluid';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { IBms } from '../../../../models/bms';
import { IEms } from '../../../../models/ems';
import { IEnergyMeterList, Role } from '../../../../models/energy-meter';
import { IInverterList } from '../../../../models/inverter';
import { Log } from '../../../../services/log';
import { Functions } from '../../../../services/shared/functions';

Chart.register(zoomPlugin);
Chart.register(annotationPlugin);
interface SelectItemGroupWithIcon extends SelectItemGroup {
  icon: string;
}

interface SelectItemWithGroup extends SelectItem {
  group: SelectItemGroups;
  getGroup: SelectItemGetGroups;
  chosenBtn: RequestedInformation;
}

enum SelectItemGroups {
  'Power',
  'Frequency',
  'Percentage',
  'Ratio',
  'Price',
  'Voltage',
  'Temperature',
}

enum SelectItemGetGroups {
  'emVolt',
  'emPower',
  'emPdq',
  'ems',
  'bms',
  'inverter',
  'frequency',
  'price',
}

interface GetLogGroups {
  getGroup: SelectItemGetGroups;
  ids: number[];
}

enum RequestedInformation {
  em_voltage,
  em_power,
  em_pdq,
  ems_soc,
  ems_net_w,
  ems_base_w,
  bms_temp,
  bms_soc,
  price,
  inverter_power,
  inverter_current_percentage,
  inverter_target_percentage,
  fcr_power,
  fcr_frequency,
}

@Component({
  selector: 'app-history',
  imports: [
    ChartModule,
    PanelModule,
    ButtonModule,
    PanelModule,
    DatePickerModule,
    Fluid,
    InputGroup,
    CardModule,
    InputGroupAddon,
    MultiSelectModule,
    FloatLabel,
    FormsModule,
    Divider,
  ],
  templateUrl: './history.html',
  styleUrl: './history.css',
})
export class History implements OnInit {
  energyMeters = input.required<IEnergyMeterList[]>();
  gridImportLimitKW = input.required<number>();
  gridExportLimitKW = input.required<number>();
  batterySystems = input.required<IEms[]>();
  bmsSystems = input.required<IBms[]>();
  //price
  inverterSystems = input.required<IInverterList[]>();
  // fcrSystems = input.required
  savingVoltagesHistory = input.required<boolean>();

  readonly limitInHours: number = 24;
  readonly locale = inject(LOCALE_ID);
  readonly logService = inject(Log);
  private customFunctions: Functions = inject(Functions);
  @ViewChild('historyChart') chartComponent!: UIChart;

  private firstLoad: boolean = true;
  zoomEnabled = false;
  data: any;
  options: any;
  labels: any[] = [];
  showToggleZoom: boolean = false;
  showGridLimitsLines: boolean = true;
  showGridLimitsLinesBtn: boolean = false;

  logDate: Date = new Date();
  maxDate: Date = new Date();

  assetList: SelectItemGroupWithIcon[] = [];
  chosenAssets: SelectItemWithGroup[] = [];

  queryGroups: GetLogGroups[] = [];

  graphEMPowerData: any[] = [];
  graphEMPdqData: any[] = [];
  graphEMVoltageData: any[] = [];
  graphEMSData: any[] = [];
  graphBMSData: any[] = [];
  // graphPriceData: any[] = [];
  graphInverterData: any[] = [];
  // graphFcrData: any[] = [];

  private requestedInfo: RequestedInformation[] = [];

  isLoading: boolean = false;

  constructor(private cd: ChangeDetectorRef) {
    effect(() => {
      const emPowerLogState = this.logService.emPowerLogState();
      if (emPowerLogState.size !== 0) {
        for (const [id, s] of emPowerLogState.entries()) {
          // if (s.error) {
          //   console.error(`Error for ID ${id}: ${s.error}`);
          //   // e.g. trigger a toast notification
          // }
          if (s.isLoading === false && !s.error) {
            this.graphEMPowerData.some((g) => g.ID === s.ID)
              ? null
              : this.customFunctions.insertInArrayByDeviceNo(
                  this.graphEMPowerData,
                  s,
                );
          }
        }
      }

      const emsLogState = this.logService.emsLogState();
      if (emsLogState.size !== 0) {
        for (const [id, s] of emsLogState.entries()) {
          // if (s.error) {
          //   console.error(`Error for ID ${id}: ${s.error}`);
          //   // e.g. trigger a toast notification
          // }
          if (s.isLoading === false && !s.error) {
            this.graphEMSData.some((g) => g.ID === s.ID)
              ? null
              : this.customFunctions.insertInArrayByDeviceNo(
                  this.graphEMSData,
                  s,
                );
          }
        }
      }

      const inverterLogState = this.logService.inverterLogState();
      if (inverterLogState.size !== 0) {
        for (const [id, s] of inverterLogState.entries()) {
          if (s.isLoading === false && !s.error) {
            this.graphInverterData.some((g) => g.ID === s.ID)
              ? null
              : this.customFunctions.insertInArrayByDeviceNo(
                  this.graphInverterData,
                  s,
                );
          }
        }
      }

      const bmsLogState = this.logService.bmsLogState();
      if (bmsLogState.size !== 0) {
        for (const [id, s] of bmsLogState.entries()) {
          if (s.isLoading === false && !s.error) {
            this.graphBMSData.some((g) => g.ID === s.ID)
              ? null
              : this.customFunctions.insertInArrayByDeviceNo(
                  this.graphBMSData,
                  s,
                );
          }
        }
      }

      const emVoltageLogState = this.logService.emVoltageLogState();
      if (emVoltageLogState.size !== 0) {
        for (const [id, s] of emVoltageLogState.entries()) {
          if (s.isLoading === false && !s.error) {
            this.graphEMVoltageData.some((g) => g.ID === s.ID)
              ? null
              : this.customFunctions.insertInArrayByDeviceNo(
                  this.graphEMVoltageData,
                  s,
                );
          }
        }
      }

      const emPdqLogState = this.logService.emPdqLogState();
      if (emPdqLogState.size !== 0) {
        for (const [id, s] of emPdqLogState.entries()) {
          if (s.isLoading === false && !s.error) {
            this.graphEMPdqData.some((g) => g.ID === s.ID)
              ? null
              : this.customFunctions.insertInArrayByDeviceNo(
                  this.graphEMPdqData,
                  s,
                );
          }
        }
      }

      if (this.logService.allLogsLoaded() && !this.firstLoad) {
        this.isLoading = false;
        this.addData();
        this.cd.detectChanges();
      }
    });
  }

  ngOnInit() {
    this.initMultiSelectList();
    this.fillXAxisData();
    this.initChart();
  }

  initMultiSelectList() {
    // Add energyMeters
    if (this.energyMeters().length > 0) {
      const energyMeterOptions: SelectItemWithGroup[] = [];

      this.energyMeters().forEach((energyMeter) => {
        if (energyMeter.available) {
          energyMeterOptions.push({
            label: `EM ${energyMeter.deviceNo} [${
              Role[energyMeter.consumptionType]
            }]`,
            value: energyMeter.ID,
            group: SelectItemGroups.Power,
            getGroup: SelectItemGetGroups.emPower,
            chosenBtn: RequestedInformation.em_power,
          });
        }
      });

      const energyMeterList: SelectItemGroupWithIcon = {
        label: 'Energy Meters',
        value: 'em',
        icon: 'pi pi-gauge',
        items: energyMeterOptions,
      };

      if (energyMeterOptions.length > 0) {
        this.assetList.push(energyMeterList);
      }
    }

    // Add PDQ of energyMeters
    if (this.energyMeters().length > 0) {
      const energyMeterPDQOptions: SelectItemWithGroup[] = [];

      this.energyMeters().forEach((energyMeter) => {
        if (energyMeter.available) {
          energyMeterPDQOptions.push({
            label: `EM ${energyMeter.deviceNo} [${
              Role[energyMeter.consumptionType]
            }] - PDQ`,
            value: energyMeter.ID,
            group: SelectItemGroups.Ratio,
            getGroup: SelectItemGetGroups.emPdq,
            chosenBtn: RequestedInformation.em_pdq,
          });
        }
      });

      const energyMeterPDQList: SelectItemGroupWithIcon = {
        label: 'Energy Meters PDQ',
        value: 'em-pdq',
        icon: 'fa-solid fa-chart-simple',
        items: energyMeterPDQOptions,
      };

      if (energyMeterPDQOptions.length > 0) {
        this.assetList.push(energyMeterPDQList);
      }
    }

    // Add voltage history option for energyMeters
    if (this.energyMeters().length > 0 && this.savingVoltagesHistory()) {
      const energyMeterVoltageOptions: SelectItemWithGroup[] = [];

      this.energyMeters().forEach((energyMeter) => {
        if (energyMeter.available) {
          energyMeterVoltageOptions.push({
            label: `EM ${energyMeter.deviceNo} [${
              Role[energyMeter.consumptionType]
            }] - Voltage`,
            value: energyMeter.ID,
            group: SelectItemGroups.Voltage,
            getGroup: SelectItemGetGroups.emVolt,
            chosenBtn: RequestedInformation.em_voltage,
          });
        }
      });

      const energyMeterVoltageList: SelectItemGroupWithIcon = {
        label: 'Energy Meters Voltage',
        value: 'em-voltage',
        icon: 'fa-solid fa-bolt',
        items: energyMeterVoltageOptions,
      };

      if (energyMeterVoltageOptions.length > 0) {
        this.assetList.push(energyMeterVoltageList);
      }
    }

    // Add EMS - SoC
    if (this.batterySystems().length > 0) {
      const batterySystemOptions: SelectItemWithGroup[] = [];

      this.batterySystems().forEach((EMS) => {
        if (EMS.available) {
          batterySystemOptions.push({
            label: `[${EMS.devNo}] - ${EMS.deviceType} - SoC`,
            value: EMS.ID,
            group: SelectItemGroups.Percentage,
            getGroup: SelectItemGetGroups.ems,
            chosenBtn: RequestedInformation.ems_soc,
          });
        }
      });

      const emsList: SelectItemGroupWithIcon = {
        label: 'EMS - State of charge',
        value: 'ems-soc',
        icon: 'fa-solid fa-car-battery',
        items: batterySystemOptions,
      };

      if (batterySystemOptions.length > 0) {
        this.assetList.push(emsList);
      }
    }

    // Add EMS - Net W
    if (this.batterySystems().length > 0) {
      const batterySystemOptions: SelectItemWithGroup[] = [];

      this.batterySystems().forEach((EMS) => {
        if (EMS.available) {
          batterySystemOptions.push({
            label: `[${EMS.devNo}] - ${EMS.deviceType} - Net W`,
            value: EMS.ID,
            group: SelectItemGroups.Power,
            getGroup: SelectItemGetGroups.ems,
            chosenBtn: RequestedInformation.ems_net_w,
          });
        }
      });

      const emsList: SelectItemGroupWithIcon = {
        label: 'EMS - New W',
        value: 'ems-net',
        icon: 'fa-solid fa-car-battery',
        items: batterySystemOptions,
      };
      if (batterySystemOptions.length > 0) {
        this.assetList.push(emsList);
      }
    }

    // Add EMS - Base W
    if (this.batterySystems().length > 0) {
      const batterySystemOptions: SelectItemWithGroup[] = [];

      this.batterySystems().forEach((EMS) => {
        if (EMS.available) {
          batterySystemOptions.push({
            label: `[${EMS.devNo}] - ${EMS.deviceType} - Base W`,
            value: EMS.ID,
            group: SelectItemGroups.Power,
            getGroup: SelectItemGetGroups.ems,
            chosenBtn: RequestedInformation.ems_base_w,
          });
        }
      });

      const emsList: SelectItemGroupWithIcon = {
        label: 'EMS - Base W',
        value: 'ems-base',
        icon: 'fa-solid fa-car-battery',
        items: batterySystemOptions,
      };
      if (batterySystemOptions.length > 0) {
        this.assetList.push(emsList);
      }
    }

    // Add BMS - Temp
    if (this.bmsSystems().length > 0) {
      const bmsSystemOptions: SelectItemWithGroup[] = [];

      this.bmsSystems().forEach((BMS) => {
        if (BMS.available) {
          bmsSystemOptions.push({
            label: `[${BMS.devNo}] - BMS - Temperature`,
            value: BMS.ID,
            group: SelectItemGroups.Temperature,
            getGroup: SelectItemGetGroups.bms,
            chosenBtn: RequestedInformation.bms_temp,
          });
        }
      });

      const bmsList: SelectItemGroupWithIcon = {
        label: 'BMS - Temperature ℃',
        value: 'bms-temp',
        icon: 'fa-solid fa-table-cells-large',
        items: bmsSystemOptions,
      };
      if (bmsSystemOptions.length > 0) {
        this.assetList.push(bmsList);
      }
    }

    // Add BMS - SoC
    if (this.bmsSystems().length > 0) {
      const bmsSystemOptions: SelectItemWithGroup[] = [];

      this.bmsSystems().forEach((BMS) => {
        if (BMS.available) {
          bmsSystemOptions.push({
            label: `[${BMS.devNo}] - BMS - SoC`,
            value: BMS.ID,
            group: SelectItemGroups.Percentage,
            getGroup: SelectItemGetGroups.bms,
            chosenBtn: RequestedInformation.bms_soc,
          });
        }
      });

      const bmsList: SelectItemGroupWithIcon = {
        label: 'BMS - State of charge',
        value: 'bms-soc',
        icon: 'fa-solid fa-table-cells-large',
        items: bmsSystemOptions,
      };

      if (bmsSystemOptions.length > 0) {
        this.assetList.push(bmsList);
      }
    }

    // Add inverter - Power
    if (this.inverterSystems().length > 0) {
      const inverterSystemOptions: SelectItemWithGroup[] = [];

      this.inverterSystems().forEach((inverter) => {
        if (inverter.available) {
          inverterSystemOptions.push({
            label: `[${inverter.deviceNo}] - ${inverter.name} - Power`,
            value: inverter.ID,
            group: SelectItemGroups.Power,
            getGroup: SelectItemGetGroups.inverter,
            chosenBtn: RequestedInformation.inverter_power,
          });
        }
      });

      const inverterList: SelectItemGroupWithIcon = {
        label: 'Inverter - Power',
        value: 'inverter-power',
        icon: 'fa-solid fa-solar-panel',
        items: inverterSystemOptions,
      };

      if (inverterSystemOptions.length > 0) {
        this.assetList.push(inverterList);
      }
    }

    // Add inverter - Current percentage
    if (this.inverterSystems().length > 0) {
      const inverterSystemOptions: SelectItemWithGroup[] = [];

      this.inverterSystems().forEach((inverter) => {
        if (inverter.available) {
          inverterSystemOptions.push({
            label: `[${inverter.deviceNo}] - ${inverter.name} - Current percentage`,
            value: inverter.ID,
            group: SelectItemGroups.Percentage,
            getGroup: SelectItemGetGroups.inverter,
            chosenBtn: RequestedInformation.inverter_current_percentage,
          });
        }
      });

      const inverterList: SelectItemGroupWithIcon = {
        label: 'Inverter - Current percentage',
        value: 'inverter-current-percentage',
        icon: 'fa-solid fa-solar-panel',
        items: inverterSystemOptions,
      };
      if (inverterSystemOptions.length > 0) {
        this.assetList.push(inverterList);
      }
    }

    // Add inverter - Target percentage
    if (this.inverterSystems().length > 0) {
      const inverterSystemOptions: SelectItemWithGroup[] = [];

      this.inverterSystems().forEach((inverter) => {
        if (inverter.available) {
          inverterSystemOptions.push({
            label: `[${inverter.deviceNo}] - ${inverter.name} - Target percentage`,
            value: inverter.ID,
            group: SelectItemGroups.Percentage,
            getGroup: SelectItemGetGroups.inverter,
            chosenBtn: RequestedInformation.inverter_target_percentage,
          });
        }
      });

      const inverterList: SelectItemGroupWithIcon = {
        label: 'Inverter - Target percentage',
        value: 'inverter-target-percentage',
        icon: 'fa-solid fa-solar-panel',
        items: inverterSystemOptions,
      };
      if (inverterSystemOptions.length > 0) {
        this.assetList.push(inverterList);
      }
    }
  }

  initChart() {
    this.data = {
      labels: this.labels,
      datasets: [
        {
          data: [],
          fill: false,
        },
      ],
    };

    this.options = this.buildOptions(this.zoomEnabled);
    this.cd.markForCheck();
  }

  fillXAxisData() {
    this.labels = [];
    const minutes = this.limitInHours * 60;
    const lastDate = new Date();
    lastDate.setHours(23);
    lastDate.setMinutes(59);
    lastDate.setSeconds(0, 0);

    for (let i = 0; i < minutes; i++) {
      const date = new Date(Date.now() - i * 60 * 60 * 1000);
      this.labels.unshift(new Date(lastDate.valueOf() - i * 60000));
    }
  }

  buildOptions(enableZoom: boolean) {
    return {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      responsive: true,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      scales: {
        x: {
          beginAtZero: true, // Set the start of the x-axis
          time: {
            tooltipFormat: 'dd/MM/yyyy HH:mm',
            unit: 'minute',
            round: 'minute',
          },
          type: 'time',
          ticks: {
            callback: (val: number, index: any, ticks: any) => {
              const date = new Date(val as number);
              const minutes = date.getMinutes();
              if (minutes % 15 === 0) {
                return formatDate(date, 'HH:mm', this.locale);
              }
              return '';
            },
          },
          afterBuildTicks: (scale: any) => {
            scale.ticks = scale.ticks.filter((tick: any) => {
              const date = new Date(tick.value);
              return date.getMinutes() % 15 === 0;
            });
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Power (kW)',
          },
          afterDataLimits(scale: any) {
            const maxAbs = Math.max(
              Math.abs(scale.min ?? 0),
              Math.abs(scale.max ?? 0),
            );
            scale.min = -maxAbs;
            scale.max = maxAbs;
          },
          position: 'left',
        },
      },
      plugins: {
        legend: {
          display: false, // Hide the legend since no data
        },
        tooltip: {
          enabled: false, // Disable tooltips
        },
        zoom: {
          pan: { enabled: enableZoom, mode: 'x' },
          zoom: {
            wheel: { enabled: enableZoom },
            pinch: { enabled: enableZoom },
            mode: 'x',
            animation: {
              duration: 1000,
              easing: 'easeOutCubic',
            },
          },
        },
      },
      pluginsArray: [
        {
          id: 'annotationHoverLabels',
          afterEvent(chart: any, args: any) {
            const event = args.event;
            const { x, y } = event;

            const annotations = chart.options.plugins.annotation?.annotations;
            if (!annotations) return;

            let changed = false;
            for (const key in annotations) {
              const ann = annotations[key];
              const scale = chart.scales[ann.scaleID];
              if (ann.type !== 'line' || !scale) {
                continue;
              }

              const yPos = scale.getPixelForValue(ann.value);
              const distance = Math.abs(y - yPos);
              const threshold = 6;
              const shouldShow = distance < threshold;

              if (ann.label && ann.label.display !== shouldShow) {
                ann.label.display = shouldShow;
                changed = true;
              }
            }

            if (changed) {
              chart.update('none');
            }
          },
        },
      ],
    };
  }

  resetZoom() {
    const chart: Chart | null = this.chartComponent.chart;
    if (chart) {
      chart.resetZoom();
    }
  }

  toggleZoom() {
    this.zoomEnabled = !this.zoomEnabled;
    this.options.plugins = {
      zoom: {
        pan: { enabled: this.zoomEnabled, mode: 'x' },
        zoom: {
          wheel: { enabled: this.zoomEnabled },
          pinch: { enabled: this.zoomEnabled },
          mode: 'x',
          animation: {
            duration: 1000,
            easing: 'easeOutCubic',
          },
        },
      },
    };

    const chart: Chart | null = this.chartComponent?.chart;
    if (chart) {
      chart.options = this.options;
      chart.update();
    }
  }

  private emptyGraphData(): void {
    this.graphBMSData = [];
    this.graphEMPdqData = [];
    this.graphEMPowerData = [];
    this.graphEMVoltageData = [];
    this.graphEMSData = [];
    // this.graphPriceData = [];
    this.graphInverterData = [];
    // this.graphFcrData = [];

    this.logService.emPowerLogState().clear();
    this.logService.emsLogState().clear();
    this.logService.inverterLogState().clear();
    this.logService.bmsLogState().clear();
    this.logService.emVoltageLogState().clear();
    this.logService.emPdqLogState().clear();
  }

  onSubmit() {
    this.isLoading = true;
    this.firstLoad = false;
    this.emptyGraphData();
    this.queryGroups = [];
    this.requestedInfo = [];
    const date = formatDate(this.logDate, 'yyyy-MM-dd', this.locale);

    const tempGroups: number[] = [];
    this.chosenAssets.forEach((chosenAsset) => {
      this.requestedInfo.some((t) => t === chosenAsset.chosenBtn)
        ? ''
        : this.requestedInfo.push(chosenAsset.chosenBtn);
      // Make sure it only gets added once
      tempGroups.some((t) => t == chosenAsset.getGroup)
        ? ''
        : tempGroups.push(chosenAsset.getGroup);
    });

    tempGroups.forEach((group) => {
      const assetsInThisGroup = this.chosenAssets.filter(
        (a) => a.getGroup === group,
      );
      const idsToGet: number[] = [];
      assetsInThisGroup.forEach((asset) => {
        idsToGet.some((i) => i == asset.value)
          ? ''
          : idsToGet.push(asset.value);
      });
      this.queryGroups.push({ getGroup: group, ids: idsToGet });
    });

    this.queryGroups.forEach((group) => {
      switch (group.getGroup) {
        case SelectItemGetGroups.emPower:
          group.ids.forEach((id) => {
            this.logService.getEmPowerLog(id, date);
          });
          break;
        case SelectItemGetGroups.emPdq:
          group.ids.forEach((id) => {
            this.logService.getEmPdqLog(id, date);
          });
          break;
        case SelectItemGetGroups.emVolt:
          group.ids.forEach((id) => {
            this.logService.getEmVoltageLog(id, date);
          });
          break;
        case SelectItemGetGroups.ems:
          group.ids.forEach((id) => {
            this.logService.getEmsLog(id, date);
          });
          break;
        case SelectItemGetGroups.bms:
          group.ids.forEach((id) => {
            this.logService.getBmsLog(id, date);
          });
          break;
        case SelectItemGetGroups.price:
          break;
        case SelectItemGetGroups.inverter:
          group.ids.forEach((id) => {
            this.logService.getInverterLog(id, date);
          });
          break;
        default:
          break;
      }
    });
  }

  addData() {
    this.zoomEnabled = true;
    this.data.datasets = [];
    this.data.labels = [];

    const { x } = this.options.scales;
    this.options.scales = { x };

    this.options.plugins = {
      legend: {
        display: true,
      },
      tooltip: {
        enabled: true,
      },
      zoom: {
        pan: { enabled: this.zoomEnabled, mode: 'x' },
        zoom: {
          wheel: { enabled: this.zoomEnabled },
          pinch: { enabled: this.zoomEnabled },
          mode: 'x',
          animation: {
            duration: 1000,
            easing: 'easeOutCubic',
          },
        },
      },
    };

    this.options.interaction = {
      intersect: false,
      mode: 'index',
    };

    if (this.graphEMPowerData.length > 0) {
      console.log(this.graphEMPowerData);
      this.addPowerData(this.graphEMPowerData, 'EM');
    }

    if (this.graphEMVoltageData.length > 0) {
      const em_voltage_phase_1_info = this.breakdownResponseToRightArray(
        this.graphEMVoltageData,
        'v1',
      );

      const em_voltage_phase_2_info = this.breakdownResponseToRightArray(
        this.graphEMVoltageData,
        'v2',
      );

      const em_voltage_phase_3_info = this.breakdownResponseToRightArray(
        this.graphEMVoltageData,
        'v3',
      );

      this.addVoltageData(em_voltage_phase_1_info, 'V1, EM');
      this.addVoltageData(em_voltage_phase_2_info, 'V2, EM');
      this.addVoltageData(em_voltage_phase_3_info, 'V3, EM');
    }

    if (this.graphEMSData.length > 0) {
      this.requestedInfo.forEach((request) => {
        switch (request) {
          case RequestedInformation.ems_base_w:
            const base_w_info = this.breakdownResponseToRightArray(
              this.graphEMSData,
              'base',
            );
            // console.log(
            //   this.batterySystems().find((x) => x.ID === base_w_info[0].ID)
            // ); // TODO: Give the name instead of "EMS"
            this.addPowerData(base_w_info, 'Base W, EMS');
            break;
          case RequestedInformation.ems_net_w:
            const net_w_info = this.breakdownResponseToRightArray(
              this.graphEMSData,
              'net',
            );
            this.addPowerData(net_w_info, 'Net W, EMS');
            break;
          case RequestedInformation.ems_soc:
            const soc_info = this.breakdownResponseToRightArray(
              this.graphEMSData,
              'soc',
            );
            this.addPercentageData(soc_info, 'SoC %, EMS');
            break;
          default:
            break;
        }
      });
    }

    if (this.graphInverterData.length > 0) {
      this.requestedInfo.forEach((request) => {
        switch (request) {
          case RequestedInformation.inverter_power:
            const inverter_power = this.breakdownResponseToRightArray(
              this.graphInverterData,
              'power',
            );
            this.addPowerData(inverter_power, 'Power, PV');
            break;
          case RequestedInformation.inverter_current_percentage:
            const inverter_current_percentage =
              this.breakdownResponseToRightArray(
                this.graphInverterData,
                'current',
              );
            this.addPercentageData(
              inverter_current_percentage,
              'Current %, PV',
            );
            break;
          case RequestedInformation.inverter_target_percentage:
            const inverter_target_percentage =
              this.breakdownResponseToRightArray(
                this.graphInverterData,
                'target',
              );
            this.addPercentageData(inverter_target_percentage, 'Target %, PV');
            break;
          default:
            break;
        }
      });
    }

    if (this.graphBMSData.length > 0) {
      this.requestedInfo.forEach((request) => {
        switch (request) {
          case RequestedInformation.bms_temp:
            const bms_temp_info = this.breakdownResponseToRightArray(
              this.graphBMSData,
              'temp',
            );
            this.addTemperatureData(bms_temp_info, 'Temperature ℃ , BMS');
            break;
          case RequestedInformation.bms_soc:
            const bms_soc_info = this.breakdownResponseToRightArray(
              this.graphBMSData,
              'soc',
            );
            this.addPercentageData(bms_soc_info, 'SoC %, BMS');
            break;
          default:
            break;
        }
      });
    }

    if (this.graphEMPdqData.length > 0) {
      this.addEmQualityData(this.graphEMPdqData, 'PDQ EM');
    }

    if (this.gridImportLimitKW() > -1) {
      this.addHorizontalLine(
        'gridLimitLine1',
        'y_power',
        this.gridImportLimitKW() * 1000,
        'Import limit (kW)',
      );
    }

    if (this.gridExportLimitKW() > -1) {
      this.addHorizontalLine(
        'gridLimitLine2',
        'y_power',
        this.gridExportLimitKW() * -1000,
        'Export limit (kW)',
      );
    }

    this.chartComponent.chart.update();
    this.showToggleZoom = true;
    this.isLoading = false;
  }

  /**
   * Function to add all the power data in the graph
   * @param powerData The array that contains the data
   * @param label string of the label (Like `EM`, `PV`, `EMS`)
   */
  addPowerData(powerData: any[], label: string) {
    if (this.options.scales.y_power === undefined) {
      this.options.scales.y_power = {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Power (W)',
        },
        afterDataLimits(scale: any) {
          const maxAbs = Math.max(
            Math.abs(scale.min ?? 0),
            Math.abs(scale.max ?? 0),
          );
          scale.min = -maxAbs;
          scale.max = maxAbs;
        },
        position:
          Object.keys(this.options.scales).length % 2 == 1 ? 'left' : 'right',
        grid: {
          drawOnChartArea:
            Object.keys(this.options.scales).length < 2 ? true : false, // only want the grid lines for one axis to show up
        },
      };
    }

    powerData.forEach((powerData, index) => {
      this.data.datasets.push({
        label: `${label} ${index + 1}`,
        pointRadius: 0, // Disables the points
        data: powerData.data,
        fill: true,
        tension: 0,
        yAxisID: 'y_power',
      });
    });
  }

  /**
   * Function to add all the power data in the graph
   * @param percentageDatas The array that contains the data
   * @param label string of the label (Like `EM`, `PV`, `EMS`)
   */
  addPercentageData(percentageDatas: any[], label: string) {
    if (this.options.scales.y_percentage === undefined) {
      this.options.scales.y_percentage = {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Percentage (%)',
        },
        afterDataLimits(scale: any) {
          scale.min = 0;
          scale.max = 100;
        },
        position:
          Object.keys(this.options.scales).length % 2 == 1 ? 'left' : 'right',
        grid: {
          drawOnChartArea:
            Object.keys(this.options.scales).length < 2 ? true : false, // only want the grid lines for one axis to show up
        },
      };
    }

    percentageDatas.forEach((percentageData, index) => {
      this.data.datasets.push({
        label: `${label} ${index + 1}`,
        pointRadius: 0, // Disables the points
        data: percentageData.data,
        fill: true,
        tension: 0,
        yAxisID: 'y_percentage',
      });
    });
  }

  /**
   * Function to add all the power data in the graph
   * @param temperatureDatas The array that contains the data
   * @param label string of the label (Like `EM`, `PV`, `EMS`)
   */
  addTemperatureData(temperatureDatas: any[], label: string) {
    if (this.options.scales.y_temperature === undefined) {
      this.options.scales.y_temperature = {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Temperature (℃)',
        },
        afterDataLimits(scale: any) {
          scale.min = 0;
          scale.max = 100;
        },
        position:
          Object.keys(this.options.scales).length % 2 == 1 ? 'left' : 'right',
        grid: {
          drawOnChartArea:
            Object.keys(this.options.scales).length < 2 ? true : false, // only want the grid lines for one axis to show up
        },
      };
    }

    temperatureDatas.forEach((temperatureData, index) => {
      this.data.datasets.push({
        label: `${label} ${index + 1}`,
        pointRadius: 0, // Disables the points
        data: temperatureData.data,
        fill: true,
        tension: 0,
        yAxisID: 'y_temperature',
      });
    });
  }

  /**
   * Function to add all the power data in the graph
   * @param voltageDatas The array that contains the data
   * @param label string of the label (Like `EM`, `PV`, `EMS`)
   */
  addVoltageData(voltageDatas: any[], label: string) {
    if (this.options.scales.y_voltage === undefined) {
      this.options.scales.y_voltage = {
        beginAtZero: true,
        title: {
          display: true,
          text: 'V (Volt)',
        },
        afterDataLimits(scale: any) {
          scale.min = 210;
          scale.max = 250;
        },
        position:
          Object.keys(this.options.scales).length % 2 == 1 ? 'left' : 'right',
        grid: {
          drawOnChartArea:
            Object.keys(this.options.scales).length < 2 ? true : false, // only want the grid lines for one axis to show up
        },
      };
    }

    voltageDatas.forEach((voltageData, index) => {
      this.data.datasets.push({
        label: `${label} ${index + 1}`,
        pointRadius: 0, // Disables the points
        data: voltageData.data,
        fill: true,
        tension: 0,
        yAxisID: 'y_voltage',
      });
    });
  }

  /**
   * Function to add all the power data in the graph
   * @param emPDWDatas The array that contains the data
   * @param label string of the label (Like `EM`, `PV`, `EMS`)
   */
  addEmQualityData(emPDQDatas: any[], label: string) {
    if (this.options.scales.y_quality === undefined) {
      this.options.scales.y_quality = {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Ratio',
        },
        afterDataLimits(scale: any) {
          scale.min = 0;
          scale.max = 1;
        },
        position:
          Object.keys(this.options.scales).length % 2 == 1 ? 'left' : 'right',
        grid: {
          drawOnChartArea:
            Object.keys(this.options.scales).length < 2 ? true : false, // only want the grid lines for one axis to show up
        },
      };
    }

    emPDQDatas.forEach((emPDQData, index) => {
      this.data.datasets.push({
        label: `${label} ${index + 1}`,
        pointRadius: 0, // Disables the points
        data: emPDQData.data,
        fill: true,
        tension: 0,
        yAxisID: 'y_quality',
      });
    });
  }

  /**
   * Function to get an array with only `x` and `y` values, instead of an array with `x`, `y`, `z`, `a`, `b` values.
   * @param array Array that will come from the backend with info
   * @param logName Name of the logging you want back (Like `soc`, `power`)
   * @returns Array with x and y values only
   */
  breakdownResponseToRightArray(array: any[], logName: string): any[] {
    const newArray = array.map((a) => {
      return { ...a };
    });

    newArray.forEach((element, index) => {
      newArray[index].data = [];
      for (let i = 0; i < array[index].data.length; i++) {
        const log = array[index].data[i];
        newArray[index].data.push({ x: log['x'], y: log[`${logName}`] });
      }
    });

    return newArray;
  }

  addHorizontalLine(
    id: string,
    scaleId: string,
    value: number,
    label: string,
    color = 'gray',
  ) {
    // Ensure annotation plugin storage exists
    if (!this.options.plugins.annotation) {
      this.options.plugins.annotation = { annotations: {} };
    }

    if (!this.scaleExists(scaleId)) {
      console.warn(`Scale '${scaleId}' does not exist on the chart.`);
      this.showGridLimitsLinesBtn = false;
      return;
    }

    this.showGridLimitsLinesBtn = true;
    const labelValue = value / 1000 < 0 ? value / -1000 : value / 1000;

    this.options.plugins.annotation.annotations[id] = {
      type: 'line',
      borderColor: color,
      borderWidth: 2,
      borderDash: [6, 6],
      scaleID: scaleId,
      value: value,
      display: true,

      label: {
        display: false,
        content: `${label}: ${labelValue} kW`,
        position: 'end',
        rotation: 0,
        borderWidth: 0,
      },
    };
  }

  scaleExists(scaleId: string): boolean {
    return this.options?.scales?.hasOwnProperty(scaleId) ?? false;
  }

  toggleGridLimitLine() {
    this.showGridLimitsLines = !this.showGridLimitsLines;
    for (const gridLimitLine in this.options.plugins.annotation.annotations) {
      const annotation =
        this.options.plugins.annotation.annotations[`${gridLimitLine}`];
      annotation.display = !annotation.display;
    }

    const chart: Chart | null = this.chartComponent?.chart;
    if (chart) {
      chart.update();
    }
  }
}
